import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { DVH_CHAINS, SALES_TYPES, type BusinessVertical } from "@graftus/schema";
import type { SectorWords } from "@/hooks/use-sector-words";
import type { LocalDraftOrganisation } from "@/lib/onboarding/local-draft";
import { StepHeading } from "../OnboardingShell";
import { StepFooter } from "../StepFooter";
import { Tag } from "@/components/ui/tag";
import { TextField } from "../ui/TextField";

export interface OrganisationValues {
  unitName: string;
  unitNumber: string;
  headcount: number;
  /** Chain key (DVH) or sales-type key (Sales). */
  choice: string;
}

const NUMBER_RE = /^[\d-]{3,11}$/;

export function validateOrganisation(values: LocalDraftOrganisation) {
  const errors: Partial<Record<keyof OrganisationValues, true>> = {};
  if (!values.unitName?.trim()) errors.unitName = true;
  if (!NUMBER_RE.test((values.unitNumber ?? "").trim())) errors.unitNumber = true;
  const headcount = Number(values.headcount);
  if (!Number.isInteger(headcount) || headcount <= 0) errors.headcount = true;
  if (!values.choice) errors.choice = true;
  return errors;
}

interface OrganisationStepProps {
  sector: BusinessVertical;
  words: SectorWords;
  initial: LocalDraftOrganisation;
  /** Every change, for the browser-side draft. */
  onDraftChange?: (values: LocalDraftOrganisation) => void;
  onSubmit: (values: OrganisationValues) => void;
  submitting: boolean;
  error: string | null;
  onBack?: () => void;
}

/** Step 3: the unit's name, number, headcount and chain or kind of selling. */
export function OrganisationStep({
  sector,
  words,
  initial,
  onDraftChange,
  onSubmit,
  submitting,
  error,
  onBack,
}: OrganisationStepProps) {
  const { t } = useTranslation("onboarding");
  const [values, setValues] = useState<LocalDraftOrganisation>({
    unitName: initial.unitName ?? "",
    unitNumber: initial.unitNumber ?? "",
    headcount: initial.headcount ?? "",
    choice: initial.choice ?? "",
  });
  const [touched, setTouched] = useState<Partial<Record<keyof OrganisationValues, boolean>>>({});
  const errors = validateOrganisation(values);
  const choices = (sector === "dvh" ? DVH_CHAINS : SALES_TYPES) as readonly string[];

  const update = (patch: LocalDraftOrganisation) => {
    setValues((prev) => {
      const next = { ...prev, ...patch };
      onDraftChange?.(next);
      return next;
    });
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched({ unitName: true, unitNumber: true, headcount: true, choice: true });
    if (Object.keys(errors).length > 0) return;
    onSubmit({
      unitName: values.unitName!.trim(),
      unitNumber: values.unitNumber!.trim(),
      headcount: Number(values.headcount),
      choice: values.choice!,
    });
  };

  return (
    <form onSubmit={submit} noValidate data-testid="onboarding-organisation">
      <StepHeading
        title={t("organisation.title", { unit: words.unit })}
        lead={t("organisation.lead", { compare: words.compare })}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <TextField
            id="onb-unit-name"
            label={words.nameL}
            placeholder={words.namePh}
            autoComplete="organization"
            value={values.unitName ?? ""}
            onChange={(e) => update({ unitName: e.currentTarget.value })}
            onBlur={() => setTouched((p) => ({ ...p, unitName: true }))}
            error={touched.unitName && errors.unitName ? t("organisation.errors.name", { nameL: words.nameL.toLowerCase() }) : null}
          />
        </div>
        <TextField
          id="onb-unit-number"
          label={words.nrL}
          placeholder={words.nrPh}
          inputMode="numeric"
          value={values.unitNumber ?? ""}
          onChange={(e) => update({ unitNumber: e.currentTarget.value })}
          onBlur={() => setTouched((p) => ({ ...p, unitNumber: true }))}
          error={touched.unitNumber && errors.unitNumber ? t("organisation.errors.number") : null}
        />
        <TextField
          id="onb-headcount"
          label={t("organisation.headcount")}
          placeholder={t("organisation.headcountPlaceholder")}
          type="number"
          inputMode="numeric"
          min={1}
          value={values.headcount ?? ""}
          onChange={(e) => update({ headcount: e.currentTarget.value })}
          onBlur={() => setTouched((p) => ({ ...p, headcount: true }))}
          error={touched.headcount && errors.headcount ? t("organisation.errors.headcount") : null}
        />
      </div>
      <fieldset className="mt-5">
        <legend className="mb-2 text-[13px] font-semibold text-g-ink-700">{words.chainL}</legend>
        <div role="radiogroup" aria-label={words.chainL} className="flex flex-wrap gap-2">
          {choices.map((key) => (
            <Tag key={key} selected={values.choice === key} onSelect={() => update({ choice: key })}>
              {words.choices[key] ?? key}
            </Tag>
          ))}
        </div>
        {touched.choice && errors.choice ? (
          <p className="mt-2 text-xs font-medium text-g-danger" role="alert">
            {t("organisation.errors.choice", { chainL: words.chainL.toLowerCase() })}
          </p>
        ) : null}
      </fieldset>
      {error ? (
        <p className="mt-5 rounded-g-md border border-g-danger bg-g-danger-tint px-4 py-3 text-[14px] font-medium text-g-danger" role="alert">
          {error}
        </p>
      ) : null}
      <StepFooter
        primaryType="submit"
        primaryLabel={submitting ? t("organisation.saving") : t("organisation.next")}
        loading={submitting}
        onBack={onBack}
      />
    </form>
  );
}
